import {
  ActionIcon,
  Button,
  Divider,
  Group,
  Loader,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowDown,
  IconArrowLeft,
  IconArrowRight,
  IconArrowUp,
  IconDeviceFloppy,
  IconSend2,
  IconUserCancel,
  IconUserExclamation,
} from "@tabler/icons-react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useFlowStore } from "../../../../../stores/WorkflowStore";
import WorkflowTemplates from "../../templates/WorkflowTemplates";
import { closeModal, modals } from "@mantine/modals";
import { handleWorkflowGet } from "../flow/flowUtils";
import { useCallback, useMemo, useState } from "react";
import NavbarToggler from "../../../../../components/View/NavbarToggler";
import { useCampaignStore } from "../../../../../stores/CampaignStore";
import { useParams, useSearchParams } from "react-router-dom";
import { useUIStore } from "../../../../../stores/UIStore";
import { editWorkflow } from "../../../../../api/WorkflowAPI";
import NodeErrors from "../flow/components/NodeErrors";
import { getNodesError } from "../flow/layouter";

const WorkflowHeadingAi = ({ activeStep, setActiveStep }) => {
  const lang = useLanguage((s) => s.language);
  const headerClosed = useUIStore((s) => s.headerClosed);
  const setHeaderClosed = useUIStore((s) => s.setHeaderClosed);
  const { campaignId } = useParams();
  const params = new URLSearchParams(location.search);
  let isReprog = !!params.get("reprogram");

  const layout = useFlowStore((s) => s.layout);
  const flowProfile = useFlowStore((s) => s.profile);
  const setProfile = useFlowStore((s) => s.setProfile);
  const setSavingWorkflow = useFlowStore((s) => s.setSavingWorkflow);
  const setToSave = useFlowStore((s) => s.setToSave);
  const setToStep = useFlowStore((s) => s.setToStep);
  const workflowError = useFlowStore((s) => s.workflowError);
  const listError = useFlowStore((s) => s.listError);
  const setListError = useFlowStore((s) => s.setListError);
  const setWorkflowError = useFlowStore((s) => s.setWorkflowError);
  const programData = useCampaignStore((s) => s.programData);

  const steps = useMemo(
    () => [
      {
        label: "Agent IA",
        value: "agent",
      },
      {
        label: "Contexte",
        value: "theme",
      },
      {
        label: "Audience",
        value: "audience",
        error: listError,
      },
      {
        label: "Stratégie",
        value: "strategy",
      },
      {
        label: lang.campaign.steps.workflow,
        value: "workflow",
        error: workflowError,
      },
      {
        label: lang.campaign.steps.review,
        value: "preview",
      },
      {
        label: lang.campaign.steps.programmation,
        value: "program",
      },
    ],
    [
      lang.campaign.steps.programmation,
      lang.campaign.steps.review,
      lang.campaign.steps.workflow,
      listError,
      workflowError,
    ],
  );

  const handleWorkflowImport = useCallback(
    (workflow_profile, handleClose) => {
      modals.openConfirmModal({
        title: lang.campaign.import.confirm.title,
        children: (
          <Text size="sm">
            {lang.campaign.import.confirm.text1} <br />
            <b>{lang.campaign.import.confirm.text2}</b>
          </Text>
        ),
        centered: true,
        labels: {
          confirm: lang.campaign.import.label,
          cancel: lang.global.cancel,
        },
        onConfirm: () => {
          closeModal();
          handleWorkflowGet({
            workflowProfile: workflow_profile,
            isEdit: false,
          });
          handleClose?.();
        },
      });
    },
    [
      lang.campaign.import.confirm.text1,
      lang.campaign.import.confirm.text2,
      lang.campaign.import.confirm.title,
      lang.campaign.import.label,
      lang.global.cancel,
    ],
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      if (activeStep === "agent") {
        document.getElementById("agent-submit").click();
      }
      if (activeStep === "theme") {
        setActiveStep("audience");
      }
      if (activeStep === "audience") {
        if (programData.contactlist_ids?.length === 0) {
          setListError(true);
        } else {
          setListError(false);
        }
        setActiveStep("strategy");
      }
      if (activeStep === "strategy") {
        document.getElementById("strategy-submit").click();
      }
      if (activeStep === "workflow") {
        let nodes = useFlowStore.getState().nodes;

        // console.log(nodes.length <= 2 && layout !== "horizontal");
        if (nodes.length <= 2 && layout !== "horizontal") {
          setWorkflowError(true);
          return setActiveStep("preview");
        }

        if (useFlowStore.getState().toSave !== "save_only") {
          setToSave("next");
        }

        setSavingWorkflow(true);
      }
      if (activeStep === "preview") {
        setActiveStep("program");
      }
      if (activeStep === "program") {
        if (listError || workflowError) {
          return modals.open({
            modalId: "workflow-error",
            title: lang.campaign.need_fix.title,
            children: (
              <Stack>
                <Text size="lg" ta={"center"}>
                  {lang.campaign.need_fix.text}
                </Text>

                {workflowError ? (
                  <Button
                    onClick={() => {
                      setActiveStep("workflow");
                      modals.close("workflow-error");
                    }}
                    size="md"
                    leftSection={<IconArrowLeft />}
                  >
                    {lang.campaign.need_fix.button_workflow}
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      setActiveStep("contacts");
                      modals.close("workflow-error");
                    }}
                    size="md"
                    leftSection={<IconArrowLeft />}
                  >
                    {lang.campaign.need_fix.button_list}
                  </Button>
                )}
              </Stack>
            ),
            styles: {
              content: {
                boxShadow: "red 0 0 12px",
                border: "1px solid red",
              },
            },
          });
        }
        document.getElementById("programmation-button").click();
      }
    },
    [
      activeStep,
      setSavingWorkflow,
      setToSave,
      programData.contactlist_ids?.length,
      setActiveStep,
      setListError,
      listError,
      lang.campaign.need_fix.title,
      lang.campaign.need_fix.text,
      lang.campaign.need_fix.button_workflow,
      lang.campaign.need_fix.button_list,
      workflowError,
      layout,
    ],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const handleStep = (step) => {
    let p = { step: step.value };
    if (isReprog) {
      p.reprogram = "true";
    }
    setSearchParams(p);
    if (activeStep === "workflow") {
      let nodes = useFlowStore.getState().nodes;
      if (nodes.length <= 2) {
        setWorkflowError(true);
      } else {
        setWorkflowError(false);
        setToStep(step.value);

        return document.getElementById("next-button").click();
      }
    }

    if (activeStep === "contacts") {
      if (programData.contactlist_ids?.length === 0) {
        setListError(true);
      } else {
        setListError(false);
      }
    }
    return setActiveStep(step.value);
  };

  const [updatingWorkflow, setUpdatingWorkflow] = useState(false);
  const handleAutoRemove = (newValue) => {
    function main() {
      setUpdatingWorkflow(true);
      editWorkflow({
        id: flowProfile.id,
        auto_remove_responders: newValue,
      })
        .then(() => {
          document.getElementById("auto_remove_responders").checked = newValue;
        })
        .catch((r) => {
          setProfile({
            ...flowProfile,
            auto_remove_responders: !newValue,
          });
        })
        .finally(() => {
          setUpdatingWorkflow(false);
        });
    }

    if (activeStep == "workflow") {
      setProfile({
        ...flowProfile,
        auto_remove_responders: newValue,
      });
    } else if (flowProfile.id) {
      const nodes = useFlowStore.getState().nodes;

      const nodesErrors = getNodesError({
        nodes: nodes.filter((x) => x.type === "action"),
        exclude_responders: newValue,
      })?.filter((x) => x.errors.length > 0);

      if (nodesErrors?.length > 0) {
        document.getElementById("auto_remove_responders").checked = !newValue;
        modals.open({
          title: lang.campaign.nodes.errors.dialog_title,
          children: (
            <NodeErrors
              errors={nodesErrors}
              handleContinue={main}
              setActive={setActiveStep}
              additionalHandleView={() => handleStep({ value: "workflow" })}
            />
          ),
          closeOnClickOutside: false,
          size: "45%",
          radius: "lg",
          styles: {
            content: {
              boxShadow: "red 0 0 12px",
              border: "1px solid red",
            },
          },
          centered: true,
          withCloseButton: false,
        });
      } else {
        main();
      }
    }
  };

  return (
    <Paper
      radius={"lg"}
      style={{ boxShadow: "#00000021 0 1px 3px" }}
      px={"md"}
      pl={4}
      w={"97%"}
      mx={"auto"}
      mt={"sm"}
      component={"form"}
      id="head-form"
      onSubmit={handleSubmit}
    >
      <Group gap={0} h={61}>
        {headerClosed && <NavbarToggler />}

        <Group wrap="nowrap" gap={"md"} ml={8}>
          <TextInput
            key={flowProfile.id}
            placeholder={lang.campaign.name}
            w={"100%"}
            name="name"
            required
            value={flowProfile.name}
            id="workflow-name"
            radius={"md"}
            size="md"
            onChange={(e) =>
              setProfile({
                ...flowProfile,
                name: e.target.value,
              })
            }
          />

          <Tooltip
            label={lang.workflows.configs.exclude_answerers}
            refProp="rootRef"
          >
            <Switch
              key={flowProfile?.id}
              size="md"
              onLabel={<IconUserCancel size={18} />}
              offLabel={<IconUserExclamation size={18} />}
              defaultChecked={flowProfile.auto_remove_responders}
              thumbIcon={updatingWorkflow ? <Loader size={12} /> : undefined}
              color={updatingWorkflow ? "gray" : "primary"}
              onChange={(e) => {
                handleAutoRemove(e.target.checked);
              }}
              id="auto_remove_responders"
              name="auto_remove_responders"
            />
          </Tooltip>

          <button
            id="workflow-config-submitter"
            type="submit"
            style={{ display: "none" }}
          />
        </Group>

        <WorkflowTemplates inWorkflow onCreated={handleWorkflowImport} />

        <Group wrap="nowrap" gap={0} ml={"auto"}>
          {steps.map((step, i) => {
            let active = step.value === activeStep;
            return (
              <Button
                key={step.value}
                variant={
                  active
                    ? "light"
                    : step.error
                      ? step.value === "contacts" && activeStep === "workflow"
                        ? "subtle"
                        : "light"
                      : "subtle"
                }
                size="sm"
                radius={"md"}
                color={
                  active
                    ? "primary"
                    : step.error
                      ? step.value === "contacts" && activeStep === "workflow"
                        ? "black"
                        : "red"
                      : "black"
                }
                onClick={() => handleStep(step)}
                leftSection={
                  <ThemeIcon
                    radius={"md"}
                    variant="outline"
                    size={"sm"}
                    fz={12}
                    color={
                      active
                        ? "primary"
                        : step.error
                          ? step.value === "contacts" &&
                            activeStep === "workflow"
                            ? "black"
                            : "red"
                          : "black"
                    }
                  >
                    {i + 1}
                  </ThemeIcon>
                }
              >
                {step.label}
              </Button>
            );
          })}

          <ActionIcon
            size="36"
            w={48}
            radius={"md"}
            type={"submit"}
            id="next-button"
          >
            {activeStep === "program" ? (
              campaignId && !isReprog ? (
                <IconDeviceFloppy size={24} />
              ) : (
                <IconSend2 size={24} />
              )
            ) : (
              <IconArrowRight size={24} />
            )}
          </ActionIcon>
          <Divider orientation="vertical" />
          <Tooltip
            label={
              headerClosed
                ? lang.workflows.header_open
                : lang.workflows.header_close
            }
          >
            <ActionIcon
              onClick={() => setHeaderClosed(!headerClosed)}
              variant="subtle"
              size={"lg"}
            >
              {headerClosed ? <IconArrowDown /> : <IconArrowUp />}
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </Paper>
  );
};

export default WorkflowHeadingAi;
